import React, { useCallback, useEffect, useState } from 'react';
import {
  LicenseAdminRow,
  ActivationAdminRow,
  NewLicenseInput,
  listLicenses,
  createLicense,
  setLicenseStatus,
  deleteLicense,
  listActivations,
  removeActivation,
  generateKey,
} from '../services/licenseAdmin';
import './Licenses.css';

const PLANS = ['monthly', 'yearly', 'lifetime', 'trial'];

// Validity presets (days). "Lifetime" → no expiry.
const VALIDITY: Record<string, number | null> = {
  monthly: 30,
  yearly: 365,
  lifetime: null,
  trial: 14,
};

const blankForm = (): NewLicenseInput => ({
  key: generateKey(),
  plan: 'yearly',
  maxDevices: 1,
  customerName: '',
  validityDays: VALIDITY.yearly,
});

// Vendor licence-management console (/licenses): create keys, change status,
// and inspect / kick the devices activated against each key.
const Licenses: React.FC = () => {
  const [rows, setRows] = useState<LicenseAdminRow[] | null>(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState<NewLicenseInput>(blankForm);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [acts, setActs] = useState<ActivationAdminRow[]>([]);

  const load = useCallback(async () => {
    setError('');
    try {
      setRows(await listLicenses());
    } catch (e) {
      setError((e as Error).message);
      setRows([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setF = <K extends keyof NewLicenseInput>(k: K, v: NewLicenseInput[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const onPlan = (plan: string) =>
    setForm((prev) => ({ ...prev, plan, validityDays: VALIDITY[plan] ?? prev.validityDays }));

  const create = async () => {
    setBusy(true);
    setError('');
    try {
      await createLicense(form);
      setForm(blankForm());
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (key: string, status: string) => {
    try {
      await setLicenseStatus(key, status);
      await load();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const remove = async (key: string) => {
    if (!window.confirm(`Delete licence ${key}? This also removes its activations.`)) return;
    try {
      await deleteLicense(key);
      if (expanded === key) setExpanded(null);
      await load();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const toggleActivations = async (key: string) => {
    if (expanded === key) {
      setExpanded(null);
      return;
    }
    setExpanded(key);
    setActs([]);
    try {
      setActs(await listActivations(key));
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const kick = async (a: ActivationAdminRow) => {
    if (!window.confirm(`Remove device "${a.device_name || a.device_id}"?`)) return;
    try {
      await removeActivation(a.id);
      setActs((prev) => prev.filter((x) => x.id !== a.id));
      await load();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');
  const expired = (d: string | null) => d != null && new Date(d).getTime() < Date.now();

  return (
    <div className="settings-page lic-admin">
      <div className="page-header">
        <h1>Licences</h1>
        <div className="header-actions">
          <button className="reset-btn" onClick={load}>
            Refresh
          </button>
        </div>
      </div>

      <div className="settings-content">
        <div className="settings-section">
          <h2>Issue a new licence</h2>
          <div className="setting-group">
            <label>
              Customer / Store Name
              <input
                value={form.customerName}
                onChange={(e) => setF('customerName', e.target.value)}
                placeholder="e.g. Sunrise Mart"
              />
            </label>
            <label>
              Plan
              <select value={form.plan} onChange={(e) => onPlan(e.target.value)}>
                {PLANS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="setting-group">
            <label>
              Max Devices
              <input
                type="number"
                min={1}
                value={form.maxDevices}
                onChange={(e) => setF('maxDevices', Math.max(1, parseInt(e.target.value, 10) || 1))}
              />
            </label>
            <label>
              Validity (days)
              <input
                type="number"
                min={0}
                value={form.validityDays ?? ''}
                placeholder="Lifetime"
                onChange={(e) =>
                  setF('validityDays', e.target.value === '' ? null : parseInt(e.target.value, 10) || 0)
                }
              />
            </label>
          </div>
          <div className="setting-group">
            <label>
              Licence Key
              <div className="lic-key-row">
                <input
                  value={form.key}
                  onChange={(e) => setF('key', e.target.value.toUpperCase())}
                  spellCheck={false}
                />
                <button className="reset-btn" type="button" onClick={() => setF('key', generateKey())}>
                  Regenerate
                </button>
              </div>
            </label>
          </div>
          <p className="muted" style={{ fontSize: 13 }}>
            Leave validity blank for a lifetime (never-expiring) licence.
          </p>
          {error && <p className="lic-admin-err">{error}</p>}
          <div className="data-actions">
            <button className="save-btn" onClick={create} disabled={busy}>
              {busy ? 'Creating…' : 'Create Licence'}
            </button>
          </div>
        </div>

        <div className="settings-section">
          <h2>All licences {rows ? `(${rows.length})` : ''}</h2>
          {rows === null ? (
            <p className="muted">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="muted">{error ? error : 'No licences yet.'}</p>
          ) : (
            <div className="lic-table-wrap">
              <table className="lic-table">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Customer</th>
                    <th>Plan</th>
                    <th>Devices</th>
                    <th>Expires</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <React.Fragment key={r.key}>
                      <tr className={expired(r.expires_at) ? 'lic-row-expired' : ''}>
                        <td className="lic-key-cell">{r.key}</td>
                        <td>{r.customer_name || '—'}</td>
                        <td>{r.plan}</td>
                        <td>
                          <button className="lic-link" onClick={() => toggleActivations(r.key)}>
                            {r.max_devices} max ▾
                          </button>
                        </td>
                        <td>
                          {fmtDate(r.expires_at)}
                          {expired(r.expires_at) && <span className="lic-tag-exp"> expired</span>}
                        </td>
                        <td>
                          <select
                            className={`lic-status lic-status-${r.status}`}
                            value={r.status}
                            onChange={(e) => changeStatus(r.key, e.target.value)}
                          >
                            <option value="active">active</option>
                            <option value="suspended">suspended</option>
                            <option value="revoked">revoked</option>
                          </select>
                        </td>
                        <td>
                          <button className="lic-del" onClick={() => remove(r.key)} title="Delete licence">
                            🗑
                          </button>
                        </td>
                      </tr>
                      {expanded === r.key && (
                        <tr className="lic-act-row">
                          <td colSpan={7}>
                            <div className="lic-acts">
                              <strong>Activated devices</strong>
                              {acts.length === 0 ? (
                                <p className="muted" style={{ margin: '6px 0 0' }}>
                                  No devices activated.
                                </p>
                              ) : (
                                <table className="lic-act-table">
                                  <thead>
                                    <tr>
                                      <th>Device</th>
                                      <th>Device ID</th>
                                      <th>Last seen</th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {acts.map((a) => (
                                      <tr key={a.id}>
                                        <td>{a.device_name || '—'}</td>
                                        <td className="lic-mono">{a.device_id}</td>
                                        <td>{new Date(a.last_seen).toLocaleString()}</td>
                                        <td>
                                          <button className="lic-del" onClick={() => kick(a)}>
                                            Remove
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Licenses;
